import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	ArrowLeft,
	ArrowRight,
	QLogo,
	ThumbsDown,
	ThumbsUp,
} from '@quizzach/assets';
import { Colors, KChip, Text, TextSize } from '@quizzach/styles';
import { BottomNavContainer, PlayNavButton } from '@quizzach/play-styles';
import { ReportIssueModal } from '@quizzach/components';
import { IQuestion } from '@quizzach/interfaces';
import { renderDifficulty } from '@quizzach/utils';

const LEVEL_TONES: Record<number, 'teal' | 'gold' | 'coral'> = {
	1: 'teal',
	2: 'gold',
	3: 'coral',
};

interface IBottomNavProps {
	type: 'plain' | 'feedback' | 'button' | 'hidden';
	handlePrevious?: VoidFunction;
	handleNext?: VoidFunction;
	children?: JSX.Element;
	question?: IQuestion;
	setFeedback?: any;
	isPremadeOrEstimation?: boolean;
}
export const BottomNav = ({
	type,
	handlePrevious,
	handleNext,
	question,
	setFeedback,
	isPremadeOrEstimation,
}: IBottomNavProps) => {
	const { t } = useTranslation();
	const [showModal, setShowModal] = useState<boolean>(false);
	const [like, setLike] = useState<boolean | null>(null);
	const [difficulty, setDifficulty] = useState<number | null>(null);

	const handleLike = (type: 'like' | 'dislike') => {
		if (type === 'like') {
			setLike(like === true ? null : true);
		} else if (type === 'dislike') {
			setLike(like === false ? null : false);
		}
	};

	const handleDifficulty = (level: 1 | 2 | 3) => {
		setDifficulty(difficulty === level ? null : level);
	};

	useEffect(() => {
		if (type === 'feedback' && setFeedback) {
			setFeedback({ liked: like, level: difficulty });
		}
	}, [like, difficulty]);

	useEffect(() => {
		if (type === 'feedback') {
			setDifficulty(null);
			setLike(null);
		}
	}, [question]);

	const levelLabel = (level: 1 | 2 | 3) =>
		level === 1
			? t('common.feedback.easy')
			: level === 2
				? t('common.feedback.medium')
				: t('common.feedback.hard');

	return (
		<>
			<ReportIssueModal
				showModal={showModal}
				question_uuid={question?.uuid || ''}
				closeModal={() => setShowModal(false)}
			/>
			<BottomNavContainer>
				<div className='bottom-nav-content'>
					<div className='bottom-nav-feedback'>
						{!isPremadeOrEstimation && type === 'feedback' && (
							<>
								<div className='thumb' onClick={() => handleLike('like')}>
									<ThumbsUp
										size={30}
										color={like === true ? Colors.textStrong : Colors.textSoft}
									/>
								</div>
								<div className='thumb' onClick={() => handleLike('dislike')}>
									<ThumbsDown
										size={30}
										color={like === false ? Colors.textStrong : Colors.textSoft}
									/>
								</div>
								<div className='level-chips'>
									{([1, 2, 3] as const).map((level) => (
										<KChip
											key={level}
											type='button'
											$on={difficulty === level}
											$tone={LEVEL_TONES[level]}
											onClick={() => handleDifficulty(level)}
										>
											{levelLabel(level)}
										</KChip>
									))}
								</div>
								{/* one (?) explains both like and difficulty feedback */}
								<div className='tooltip qmark'>
									<QLogo size={10} />
									<span className='tooltiptext'>
										<Text color={Colors.textSoft} $size={TextSize.xs}>
											{t('quiz.break.feedbackMessage')}
										</Text>
										<Text color={Colors.textMuted} $size={TextSize.xs}>
											{t('common.feedback.currentUserEstimate')}{' '}
											{renderDifficulty(question?.level || 0)}
										</Text>
									</span>
								</div>
							</>
						)}
					</div>

					<div className='bottom-nav-center'>
						<PlayNavButton
							type='button'
							aria-label={t('quiz.options.previousSlide')}
							onClick={handlePrevious}
						>
							<ArrowLeft size={24} />
						</PlayNavButton>

						<PlayNavButton
							type='button'
							className='pri'
							aria-label={t('quiz.options.nextSlide')}
							onClick={handleNext}
						>
							<ArrowRight size={26} />
						</PlayNavButton>
					</div>

					<div className='bottom-nav-actions'>
						<div
							className='bottom-nav-report'
							onClick={() => setShowModal(true)}
						>
							<Text $alternative color={Colors.textMuted} $size={TextSize.xs}>
								{t('common.reportIssue')}
							</Text>
						</div>
					</div>
				</div>
			</BottomNavContainer>
		</>
	);
};
